/**
 * WorkspaceSettings — 워크스페이스 단위 정책/기본값
 *
 * 단일 테이블(workspace_settings) 에 정책을 모아 둠.
 * 신규 정책 추가 시 인터페이스 + DEFAULTS + service.upsert 로 확장.
 */
import { WritePermission, CommentPermission, NotifyPermission } from './Board';

/** 새 보드 권한 기본값 — BoardCreateModal 의 초기 state.
 *  @개인 멘션은 별도 권한 분류 없이 작성권한과 자동 일치 → 필드 없음. */
export interface BoardDefaults {
  write_permission: WritePermission;
  comment_permission: CommentPermission;
  notify_all_permission: NotifyPermission;
}

/** GIGAPOD 바로가기 연동 — 좌측 모듈 레일의 외부 링크 버튼. */
export interface GigapodSettings {
  /** 좌측 메뉴에 GIGAPOD 링크 버튼을 노출할지 */
  enabled: boolean;
  /** 버튼 클릭 시 이동할 외부 URL (http/https) */
  url: string;
}

export interface WorkspaceSettings {
  workspace_id: string;
  board_defaults: BoardDefaults;
  /** 보드 관리 권한을 위임받은 member-role 사용자 ID 목록. owner/admin 은 자동이므로 미포함. */
  board_manager_user_ids: string[];
  gigapod: GigapodSettings;
  updated_at: string;
}

export const DEFAULT_BOARD_DEFAULTS: BoardDefaults = {
  write_permission: 'members',
  comment_permission: 'everyone',
  notify_all_permission: 'admin-only',
};

export const DEFAULT_GIGAPOD_SETTINGS: GigapodSettings = {
  enabled: false,
  url: '',
};

/**
 * 사용자가 입력한 GIGAPOD URL 정규화 — 스킴을 빠뜨린 입력(`my-gigapod.jp`)을 https 로 보정.
 * 이미 스킴이 있으면 그대로 두어 http:// 나 잘못된 스킴이 검증에 걸리도록 한다.
 */
export function normalizeGigapodUrl(raw: string): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return '';
  return /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/**
 * 레일 버튼 노출·이동에 쓸 수 있는 URL 인지 검사.
 * http/https 로 한정 — `javascript:` 등 스킴이 링크로 새어 나가지 않게 막는다.
 */
export function isValidGigapodUrl(url: string): boolean {
  const trimmed = (url ?? '').trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(trimmed);
    return (parsed.protocol === 'https:' || parsed.protocol === 'http:') && !!parsed.hostname;
  } catch {
    return false;
  }
}

/** 좌측 레일에 GIGAPOD 버튼을 실제로 그릴지 — 체크 ON + URL 유효 둘 다 만족해야 함. */
export function shouldShowGigapodLink(gigapod: GigapodSettings | undefined): boolean {
  return !!gigapod?.enabled && isValidGigapodUrl(gigapod.url);
}

/** 미설정 워크스페이스에 대해 반환하는 기본 settings */
export function defaultWorkspaceSettings(workspaceId: string): WorkspaceSettings {
  return {
    workspace_id: workspaceId,
    board_defaults: { ...DEFAULT_BOARD_DEFAULTS },
    board_manager_user_ids: [],
    gigapod: { ...DEFAULT_GIGAPOD_SETTINGS },
    updated_at: new Date().toISOString(),
  };
}

/** Supabase row → WorkspaceSettings 변환. null/누락 필드는 default 로 채움. */
export function rowToWorkspaceSettings(row: any): WorkspaceSettings {
  const defs = row?.board_defaults ?? {};
  const giga = row?.gigapod ?? {};
  return {
    workspace_id: row.workspace_id,
    board_defaults: {
      write_permission:        (defs.write_permission        ?? DEFAULT_BOARD_DEFAULTS.write_permission) as WritePermission,
      comment_permission:      (defs.comment_permission      ?? DEFAULT_BOARD_DEFAULTS.comment_permission) as CommentPermission,
      notify_all_permission:   (defs.notify_all_permission   ?? DEFAULT_BOARD_DEFAULTS.notify_all_permission) as NotifyPermission,
    },
    board_manager_user_ids: Array.isArray(row.board_manager_user_ids) ? row.board_manager_user_ids : [],
    gigapod: {
      enabled: giga.enabled === true,
      url: typeof giga.url === 'string' ? giga.url : DEFAULT_GIGAPOD_SETTINGS.url,
    },
    updated_at: row.updated_at ?? new Date().toISOString(),
  };
}
